import { ATTRIBUTE_LIMITS, ATTRIBUTE_OBJECTS, ATTRIBUTE_TYPES, type AttributeObject, type AttributeType } from "@sagot/constants";
import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, Select, Switch, Textarea, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useAttributeMutations, type AttributeDefinition } from "../../shared";
import { aiVisibleLock, draftOf, emptyDraft, hasProblems, inputOf, problemsOf, slugKey, type AiVisibleLock, type AttributeDraft, type AttributeProblems } from "../attributes-model";

const A = "settings.attributes";
const AI = "customerContext.aiVisible";

interface AttributeDialogProps {
  /** The attribute to edit; `null` creates one. */
  attribute: AttributeDefinition | null;
  onClose: () => void;
}

/** "AI can see this" (PDI-007): off by default; personal data says so and stays off until turned on; a field hidden from the assistant is locked off. */
function AiVisibleField({ checked, lock, onChange }: { checked: boolean; lock: AiVisibleLock; onChange: (on: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 rounded-md border border-line p-3" data-testid="ai-visible-field">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <label htmlFor="attribute-ai-visible" className="text-sm font-semibold">{t(`${AI}.label`)}</label>
          <p id="attribute-ai-visible-hint" className="mt-0.5 text-xs text-fg-subtle">{t(`${AI}.hint`)}</p>
        </div>
        <Switch id="attribute-ai-visible" aria-describedby="attribute-ai-visible-hint" checked={checked && lock !== "hidden"} disabled={lock === "hidden"} onCheckedChange={onChange} className="mt-0.5" />
      </div>
      {lock ? (
        <p className="flex items-start gap-2 rounded-md bg-warning-soft p-2 text-xs" data-testid={`ai-visible-${lock}`}>
          <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
          {t(`${AI}.${lock}`)}
        </p>
      ) : null}
    </div>
  );
}

/** Create or edit a custom attribute. The object, key and type are fixed once it exists (stored values depend on them). */
export function AttributeDialog({ attribute, onClose }: AttributeDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { create, update } = useAttributeMutations();
  const [draft, setDraft] = useState<AttributeDraft>(attribute ? draftOf(attribute) : emptyDraft());
  const [keyEdited, setKeyEdited] = useState(attribute !== null);
  const [shown, setShown] = useState<AttributeProblems>({});
  const [keyTaken, setKeyTaken] = useState(false);
  const editing = attribute !== null;
  const lock = attribute ? aiVisibleLock(attribute) : null;
  const busy = create.isPending || update.isPending;

  const setLabel = (label: string) => {
    setDraft((d) => ({ ...d, label, key: keyEdited ? d.key : slugKey(label) }));
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const problems = problemsOf(draft);
    setShown(problems);
    if (hasProblems(problems)) return;
    const body = inputOf(draft);
    try {
      if (attribute) await update.mutateAsync({ id: attribute.id, body: { label: body.label, options: body.options, ...(attribute.object === "visitor" ? { ai_visible: body.ai_visible } : {}) } });
      else await create.mutateAsync(body);
      toast.success(t(`${A}.saved`));
      onClose();
    } catch (caught) {
      if (isApiError(caught) && caught.status === 409) setKeyTaken(true);
      else toast.error(message(caught));
    }
  };
  const keyError = keyTaken ? t(`${A}.keyTaken`) : shown.key ? t(`${A}.keyInvalid`) : undefined;
  const optionsError = shown.optionLength ? t(`${A}.optionTooLong`, { max: ATTRIBUTE_LIMITS.option }) : shown.options ? t(`${A}.optionsRequired`, { max: ATTRIBUTE_LIMITS.options }) : undefined;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t(editing ? `${A}.editTitle` : `${A}.addTitle`)} description={t(`${A}.dialogHint`)} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t(`${A}.object`)}>
              <Select value={draft.object} disabled={editing} onChange={(e) => { setDraft((d) => ({ ...d, object: e.target.value as AttributeObject })); }}>
                {ATTRIBUTE_OBJECTS.map((object) => <option key={object} value={object}>{t(`${A}.objects.${object}`)}</option>)}
              </Select>
            </Field>
            <Field label={t(`${A}.type`)}>
              <Select value={draft.type} disabled={editing} onChange={(e) => { setDraft((d) => ({ ...d, type: e.target.value as AttributeType })); }}>
                {ATTRIBUTE_TYPES.map((type) => <option key={type} value={type}>{t(`${A}.types.${type}`)}</option>)}
              </Select>
            </Field>
          </div>
          <Field label={t(`${A}.label`)} error={shown.label ? t(`${A}.labelRequired`) : undefined}>
            <Input maxLength={ATTRIBUTE_LIMITS.label} placeholder={t(`${A}.labelPlaceholder`)} value={draft.label} onChange={(e) => { setLabel(e.target.value); }} />
          </Field>
          <Field label={t(`${A}.key`)} hint={t(`${A}.keyHint`)} error={keyError}>
            <Input
              className="font-mono text-xs"
              maxLength={ATTRIBUTE_LIMITS.key}
              disabled={editing}
              value={draft.key}
              onChange={(e) => {
                setKeyEdited(true);
                setKeyTaken(false);
                setDraft((d) => ({ ...d, key: e.target.value }));
              }}
            />
          </Field>
          {draft.type === "list" ? (
            <Field label={t(`${A}.options`)} hint={t(`${A}.optionsHint`, { max: ATTRIBUTE_LIMITS.options })} error={optionsError}>
              <Textarea rows={5} value={draft.options} onChange={(e) => { setDraft((d) => ({ ...d, options: e.target.value })); }} />
            </Field>
          ) : null}
          {draft.object === "visitor" ? <AiVisibleField checked={draft.aiVisible} lock={lock} onChange={(aiVisible) => { setDraft((d) => ({ ...d, aiVisible })); }} /> : null}
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" loading={busy}>{t(`${A}.save`)}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
